import { getAvailableStorageList, storeFileToServer } from '@api/services/box-storage.service';
import DocFileIcon from '@components/DocFileIcon/DocFileIcon';
import RequiredMark from '@components/FormFields/RequiredMark';
import { useUploadNavigationGuard } from '@hooks/useUploadNavigationGuard';
import { Button, Card, CircularProgress, HelpText, Label } from '@hyperflake/react-ui-library';
import { InfoCircleIcon, UploadIcon } from '@icon/index';
import { FormHelperText } from '@library/forms';
import SingleCategoryInput from '@library/forms/components/SingleInputSelector/SingleInputSelector';
import { useQuery } from '@tanstack/react-query';
import { getResellerIdFromRedux } from '@utils/common';
import { getFileExtension } from '@utils/file-utils';
import { ErrorMessage, Form, Formik } from 'formik';
import http from 'httpConfig/http';
import useCategoryListData from 'modules/box-strorage/hooks/useCategoryListData';
import {
    MultipartCompletePayload,
    MultipartInitPayload,
    MultipartInitResponse,
    MultipartPartUrlPayload,
    MultipartPartUrlResponse,
    uploadMultipartFile,
} from 'modules/documents/helpers/multipart.util';
import { FormikInput, FormikSelect } from 'modules/shared/features/formik';
import { useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

type UploadRouteState = {
    currentPrefix?: string;
};

const validationSchema = Yup.object().shape({
    storage: Yup.string().required('Storage is required'),
    fileName: Yup.string().required('File name is required'),
    category: Yup.string(),
    files: Yup.array()
        .of(Yup.mixed().required())
        .max(1, 'Only one file is allowed')
        .test('fileRequired', 'File is required', (files) => files?.length === 1),
});

const DocumentUploadForm = () => {
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const navigate = useNavigate();
    const location = useLocation();

    const { currentPrefix = '' } = (location.state as UploadRouteState) || {};
    const normalizedPrefix = currentPrefix && !currentPrefix.endsWith('/') ? `${currentPrefix}/` : currentPrefix;

    const inputRef = useRef<HTMLInputElement>(null);

    const { category, isLoadingCategory } = useCategoryListData();

    const { data: storages, isLoading: storageIsLoading } = useQuery<string[]>(['storage-names'], async () =>
        getAvailableStorageList()
    );

    async function createMultipartUpload(payload: MultipartInitPayload) {
        const { data } = await http.post(
            `${import.meta.env.VITE_APP_API_URL}/${getResellerIdFromRedux()}/box-storage/multipart/init`,
            payload
        );

        return data as MultipartInitResponse;
    }

    async function getMultipartUploadPartUrl(payload: MultipartPartUrlPayload) {
        const { data } = await http.post(
            `${import.meta.env.VITE_APP_API_URL}/${getResellerIdFromRedux()}/box-storage/multipart/part-url`,
            payload
        );

        return data as MultipartPartUrlResponse;
    }

    async function completeMultipartUpload(payload: MultipartCompletePayload) {
        const { data } = await http.post(
            `${import.meta.env.VITE_APP_API_URL}/${getResellerIdFromRedux()}/box-storage/multipart/complete`,
            payload
        );

        return data;
    }

    const storageOption = useMemo(() => {
        if (!storages) return [];
        return [{ label: 'Select Storage', value: '' }, ...storages.map((val) => ({ label: val, value: val }))];
    }, [storages]);

    const initialValues = {
        storage: '',
        fileName: '',
        category: '',
        files: [] as File[],
    };

    const handleSubmit = async (values: typeof initialValues) => {
        try {
            const file = values.files?.[0];
            if (!file) return;

            const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
            const contentType = file.type || 'application/octet-stream';

            setUploadProgress(0);

            const { key } = await uploadMultipartFile({
                file,
                storage: values.storage,
                fileName: values.fileName,
                contentType,
                extension,
                category: values.category,
                createMultipartUpload,
                getMultipartUploadPartUrl,
                completeMultipartUpload,
                onProgress: setUploadProgress,
            });

            // optional metadata call
            // await storeFileToServer({
            //     filename: values.fileName,
            //     key,
            //     category: values.category,
            //     storage: values.storage,
            // });

            toast.success('Upload complete');
            setUploadProgress(0);
            navigate('/box-storage');
        } catch (err: any) {
            console.error(err);
            toast.error(err?.message || 'Upload failed');
            setUploadProgress(0);
        }
    };

    return (
        <>
            <div className="mb-3 text-sm text-grayscale-600">
                Uploading to: <span className="font-medium text-grayscale-800">/{normalizedPrefix}</span>
            </div>

            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                {({ values, setFieldValue, isSubmitting }) => (
                    <Form>
                        <Card className="mt-4">
                            <Card.Header>
                                <h6>Documents</h6>
                            </Card.Header>

                            <Card.Body>
                                <div>
                                    <Label>
                                        Storage <RequiredMark />
                                    </Label>
                                    <div className="flex gap-2">
                                        <FormikSelect name="storage" options={storageOption} />
                                        {storageIsLoading && <CircularProgress size={14} />}
                                    </div>
                                    <FormHelperText variant="error">
                                        <ErrorMessage name="storage" />
                                    </FormHelperText>
                                </div>

                                <div
                                    className="relative flex justify-center cursor-pointer bg-primary/10 py-6 rounded-lg mt-4"
                                    onClick={() => inputRef.current?.click()}
                                >
                                    <>
                                        <Button type="button" variant="shaded" size="xs" className="cursor-pointer">
                                            <UploadIcon />
                                            Upload File
                                        </Button>

                                        <input
                                            ref={inputRef}
                                            type="file"
                                            accept="*/*"
                                            className="hidden"
                                            onClick={(e) => {
                                                e.currentTarget.value = '';
                                            }}
                                            onChange={(event) => {
                                                const selectedFiles = event.currentTarget.files;
                                                if (selectedFiles?.[0]) {
                                                    setFieldValue('files', [selectedFiles[0]]);
                                                    setFieldValue('fileName', selectedFiles[0].name);
                                                }
                                            }}
                                        />
                                    </>
                                </div>

                                <ul className="mt-6 list-disc pl-5">
                                    {values.files.map((file, idx) => (
                                        <li key={idx} className="flex items-center gap-2">
                                            <DocFileIcon className="w-7" extension={getFileExtension(file.name)} />
                                            {file.name}
                                            <Button
                                                size="xs"
                                                type="button"
                                                className="ml-4"
                                                onClick={() => {
                                                    setFieldValue('files', []);
                                                    setFieldValue('fileName', '');
                                                }}
                                            >
                                                Remove
                                            </Button>
                                        </li>
                                    ))}
                                </ul>

                                <div className="mt-4">
                                    <Label>File Name</Label>
                                    <FormikInput name="fileName" />
                                    <HelpText variant="error">
                                        <ErrorMessage name="fileName" />
                                    </HelpText>
                                </div>

                                <div className="mt-4 w-full">
                                    <Label>Category</Label>
                                    <div className="relative">
                                        <SingleCategoryInput
                                            id="category"
                                            categories={!isLoadingCategory ? category : []}
                                            disabled={isLoadingCategory}
                                            selectedCategory={values.category}
                                            onSelectedCategoryChange={(cat) => setFieldValue('category', cat)}
                                        />
                                        {isLoadingCategory && (
                                            <div className="absolute right-3 text-primary">
                                                <span className="flex">
                                                    <CircularProgress size={16} />
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <FormHelperText variant="error">
                                        <ErrorMessage name="category" />
                                    </FormHelperText>
                                </div>

                                {uploadProgress > 0 && (
                                    <>
                                        <div className="mt-2 text-sm text-primary">Uploading: {uploadProgress}%</div>
                                        <div className="mt-2 text-sm text-warning-500 flex items-center gap-2">
                                            <div>
                                                <InfoCircleIcon />
                                            </div>
                                            <div>
                                                Please note: Refreshing or closing this page will interrupt and cancel
                                                the ongoing upload process.
                                            </div>
                                        </div>
                                    </>
                                )}
                            </Card.Body>
                        </Card>

                        <Button type="submit" className="mt-6" disabled={isSubmitting || values.files.length === 0}>
                            {isSubmitting ? 'Uploading...' : 'Upload'}
                        </Button>
                    </Form>
                )}
            </Formik>
        </>
    );
};

export default DocumentUploadForm;
